import { Button, Input } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from '@/icons';
import { vars } from 'style/theme.css'
import { useNavigate } from 'react-router-dom';
import Pagination from 'components/atoms/Pagination/Pagination';
import { useEffect, useState } from 'react';
import { studentExamApi } from '@/services/api';
import { formatNumber } from '@/utils/number';
import { Dialog } from '@/components/organisms/Modal/Dialog';

interface Exam {
    id: number            // ticketId (프론트 key 용)
    paperId: number       // 실제 시험지 ID (start 용)
    name: string
    deadline: string
    completedDate?: string
    assignedDate?: string
    status: '응시하기' | '채점중' | '채점완료' | '기간만료' | '경고종료'
    examResultId?: number | null
  }
  
const PAGE_SIZE = 10;

// API 데이터 바인딩용 상태
  
const STATUS_PRIORITY: Record<Exam['status'] | string, number> = {
  '응시하기': 0,
  '채점중': 1,
  '경고종료': 2,
  '채점완료': 3,
  '기간만료': 4,
};


export default function StudentExamList() {
  const navigate = useNavigate();
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState<Exam[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isNameConfirmOpen, setIsNameConfirmOpen] = useState(false);
  const [pendingExam, setPendingExam] = useState<Exam | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [nameError, setNameError] = useState('');
  const [studentName, setStudentName] = useState('');

  const parseDateTimestamp = (value?: string | null) => {
    if (!value) return null;
    const normalized = value.replace(/\./g, '-');
    const timestamp = Date.parse(normalized);
    return Number.isNaN(timestamp) ? null : timestamp;
  };

  const fetchList = async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await studentExamApi.list({ search: searchTerm, onlyAvailable: showOnlyAvailable });
      const data = res?.data?.list || res?.list || [];
      const mapped: Exam[] = (data as any[]).map((it) => ({
        id: Number(it.id ?? it.ticketId ?? it.paperId),              // 고유 key: ticketId 우선
        paperId: Number(it.paperId ?? it.id ?? it.ticketId),         // 시험 시작 시 사용
        name: String(it.name || ''),
        deadline: String(it.deadline || ''),
        completedDate: it.completedDate ? String(it.completedDate) : undefined,
        assignedDate: it.assignedDate ? String(it.assignedDate) : undefined,
        status: (it.status as Exam['status']) || '응시하기',
        examResultId: it.examResultId ? Number(it.examResultId) : null,
      }));
      mapped.sort((a, b) => {
        const rankA = STATUS_PRIORITY[a.status] ?? 99;
        const rankB = STATUS_PRIORITY[b.status] ?? 99;
        if (rankA !== rankB) {
          return rankA - rankB;
        }

        const deadlineA = parseDateTimestamp(a.deadline);
        const deadlineB = parseDateTimestamp(b.deadline);
        if (deadlineA !== deadlineB) {
          if (deadlineA === null) return 1;
          if (deadlineB === null) return -1;
          return deadlineA - deadlineB;
        }

        const assignedA = parseDateTimestamp(a.assignedDate);
        const assignedB = parseDateTimestamp(b.assignedDate);
        if (assignedA !== assignedB) {
          if (assignedA === null) return 1;
          if (assignedB === null) return -1;
          return assignedB - assignedA;
        }

        return b.id - a.id;
      });
      setRows(mapped);
    } catch (e) {
      setError('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { fetchList(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, showOnlyAvailable]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showOnlyAvailable]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [rows.length, currentPage]);

  const handleRowClick = (row: Exam) => {
    if (row.status === '응시하기') {
      openNameConfirmModal(row);
      return;
    }
    // 채점중/채점완료 상태면 상세 페이지로 이동
    if (row.status === '채점중' || row.status === '채점완료') {
      if (row.examResultId) {
        navigate(`/mypage/exam/detail?examResultId=${row.examResultId}`);
      }
      return;
    }
    
    // 기간만료는 클릭 불가
    if (row.status === '기간만료' || row.status === '경고종료') {
      return;
    }
    
    // 응시하기 상태면 시험 페이지로 이동
    navigate(`/exam/paper?id=${row.paperId}`);
  };

  const readStudentNameFromStorage = () => {
    if (typeof window === 'undefined') return '';
    const displayName = localStorage.getItem('student_display_name') || '';
    const loginName = localStorage.getItem('student_user_name') || '';
    return (displayName || loginName || '').trim();
  };

  useEffect(() => {
    setStudentName(readStudentNameFromStorage());
  }, []);

  const handleToggleAvailable = () => {
    setShowOnlyAvailable(!showOnlyAvailable);
  };

  const openNameConfirmModal = (row: Exam) => {
    setPendingExam(row);
    setNameInput('');
    setNameError('');
    setIsNameConfirmOpen(true);
  };

  const closeNameConfirmModal = () => {
    setIsNameConfirmOpen(false);
    setPendingExam(null);
    setNameInput('');
    setNameError('');
  };

  const getStoredStudentName = () => {
    if (studentName) return studentName;
    return readStudentNameFromStorage();
  };

  const handleConfirmExamStart = () => {
    const expectedName = getStoredStudentName().trim();
    const typedName = nameInput.trim();
    if (!typedName) {
      setNameError('이름을 입력해주세요.');
      return;
    }
    if (!expectedName) {
      window.alert('학생 이름 정보를 찾을 수 없습니다.');
      return;
    }
    if (typedName !== expectedName) {
      setNameError('이름이 일치하지 않습니다.');
      window.alert('이름이 일치하지 않습니다.');
      return;
    }
    closeNameConfirmModal();
    if (pendingExam) {
      navigate(`/exam/paper?id=${pendingExam.paperId}`);
    }
  };

  const renderStatusButton = (row: Exam) => {
    switch (row.status) {
      case '응시하기':
        return (
          <Button
            type='button'
            className='w-18'
            size='small'
            onClick={(event) => {
              event.stopPropagation();
              openNameConfirmModal(row);
            }}
          >
            응시하기
          </Button>
        );
      case '채점중':
        return <Button type='button' className='w-18' size="small" intent='secondary'>채점중</Button>;
      case '채점완료':
        return <Button type='button' className='w-18' size="small" outline>채점완료</Button>;
      case '기간만료':
        return <Button type='button' className='w-18' intent='secondary' outline size="small" disabled>기간만료</Button>;
      case '경고종료':
        return <Button type='button' className='w-24' intent='secondary' outline size="small" disabled>경고종료</Button>;
      default:
        return <span>-</span>;
    }
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const totalCount = rows.length;

  const columns = [
    {
      key: 'id' as const,
      render: (_row: Exam, rowIndex: number) => formatNumber(totalCount - (startIndex + rowIndex)),
    },
    { key: 'name'   as const, render: (row: Exam, _rowIndex: number) => row.name },
    { key: 'deadline' as const, render: (row: Exam, _rowIndex: number) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">응시마감일</span>
        <span>{row.deadline}</span>
      </div>
    ) },
    { key: 'completedDate' as const, render: (row: Exam, _rowIndex: number) => (
      <div className="flex gap-2 items-center lg:block">
        <span className="text-xs text-gray-400 lg:hidden">응시완료일</span>
        <span>{row.completedDate || '-'}</span>
      </div>
    ) },
    { key: 'status' as const, render: (row: Exam, _rowIndex: number) => renderStatusButton(row) },
  ];

  const paginatedRows = rows.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
        <main className='flex flex-col w-full'>
            <section className='flex flex-col gap-2 items-start pt-3 w-full lg:px-5 lg:flex-row lg:justify-between lg:items-center'>
                <Input
                placeholder='검색어를 입력해주세요.'
                className='max-w-[500px]'
                value={searchTerm}
                onChange={(e)=>setSearchTerm(e.target.value)}
                icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
                <button 
                  className={`flex gap-1 justify-center items-center text-sm ${showOnlyAvailable ? 'text-indigo-500' : 'text-gray-500'}`}
                  onClick={handleToggleAvailable}
                >
                    <Icon name="check" size={20} className={showOnlyAvailable ? '*:fill-indigo-500' : '*:fill-gray-400'} />
                    <span>응시 가능한 시험만 보기</span>
                </button>
            </section>

            <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
                {/* no_list */}
                {/* <p>리스트가 없습니다.</p> */}
                <div className='w-full'>
                    {isLoading ? (
                        <div className='py-12 text-center text-gray-500'>
                            로딩 중...
                        </div>
                    ) : rows.length === 0 ? (
                        <div className='py-12 text-center text-gray-500'>
                            {showOnlyAvailable ? '현재 응시 가능한 학습이 없습니다.' : '배정된 학습이 없습니다.'}
                        </div>
                    ) : (
                        <>
                    <ul className={`${styles.headerList} w-full`}>
                        <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                        <li className={`${styles.headerItem}`}>시험명</li>
                        <li className={`${styles.headerItem} max-w-[200px]`}>응시마감일</li>
                        <li className={`${styles.headerItem} max-w-[200px]`}>응시완료일</li>
                        <li className={`${styles.headerItem} max-w-[200px] lg:flex justify-center`}>상태</li>
                    </ul>
                    <div className='flex flex-col gap-4 w-full lg:gap-0'>
                        {paginatedRows.map((row, rowIndex) => {
                            const isClickable = row.status === '응시하기' || row.status === '채점중' || row.status === '채점완료';
                            return (
                            <ul 
                                key={row.id} 
                                className={`${styles.bodyList} lg:shadow-none table-card transition-colors !grid grid-cols-2 lg:!flex ${
                                    isClickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-75'
                                }`}
                                onClick={() => handleRowClick(row)}
                            >
                            {columns.map(({ key, render }) => (
                                <li
                                key={key}
                                className={`${styles.bodyItem} ${
                                    key === 'id' ? 'max-w-[80px] !hidden lg:!flex justify-center' :
                                    key === 'name' ? 'col-span-2 lg:col-span-1 ' :
                                    key === 'deadline' ? 'text-sm text-gray-500 max-w-[200px] lg:static  col-span-1' :
                                    key === 'completedDate' ? 'text-sm text-gray-500 max-w-[200px] lg:static  col-span-1' :
                                    key === 'status' ? 'absolute top-5 right-5 lg:static max-w-[200px] lg:flex justify-center' :
                                    ''
                                }`}
                                >
                                {render(row, rowIndex)}
                                </li>
                            ))}
                            </ul>
                            );
                        })}
                    </div>
                        </>
                    )}
                </div>
                    <div className='py-5'>
                <Pagination
                  total={rows.length}
                  pageSize={PAGE_SIZE}
                  current={currentPage}
                  onChange={handlePageChange}
                />
                </div>
            </section>
        <Dialog
          open={isNameConfirmOpen}
          onCancel={closeNameConfirmModal}
          onOk={handleConfirmExamStart}
          okText="응시하기"
          cancelText="취소"
        >
          <div className='flex flex-col gap-3 pt-[10px] px-[10px]'>
            <p className='text-sm text-gray-600'>
              응시 전 본인 확인을 위해 이름을 입력해주세요. 입력한 이름이 계정 정보와 일치해야 시험을 시작할 수 있습니다.
            </p>
            <Input
              placeholder='이름을 입력해주세요.'
              value={nameInput}
              onChange={(e) => {
                setNameInput(e.target.value);
                if (nameError) setNameError('');
              }}
            />
            {nameError && <p className='text-xs text-red-500'>{nameError}</p>}
          </div>
        </Dialog>
        </main>
  )
}